import {pipe} from "fp-ts/lib/function";
import {array, option, record} from "fp-ts";
import {Rubriek, RubriekReducer} from "domain/store/rubriekenReducer";
import {rubriekenFp} from "domain/rubriek";
import {last} from "fp-ts/lib/Semigroup";
import {Value} from "domain/types";
import {Aangifte} from "domain/aangifte";

interface Props {
  company: string;
  locale: "fr" | "nl";
  aangifte: Aangifte;
}

export interface RubriekOptimalisatieInfo {
  Code: string;
  NonOptimized: number;
  Optimized: number;
}
export interface OptimalisatieInfo {
  OptimalisatieUitgevoerd: boolean;
  OptimalisatieBsLtsKapMogelijk: boolean;
  OptimalisatieWoonBonusMogelijk: boolean;
  RubriekOptimalisaties: RubriekOptimalisatieInfo[];
}

export interface CalcJsonResult {
  Versie: string;
  DetailBerekeningHtml: string;
  TeBetalenBelastingHtml: string;
  BesparingLangeTermijn: number;
  BesparingPensioensparen: number;
  ValidatieFouten: string[];
  AangifteInfo: string[];
  Opmerkingen: string[];
  VoorafBetalingen: { [rubriek: number]: string; };
  Rubrieken: { [rubriek: number]: number; };
  RubriekenVrijgesteld: { [rubriek: number]: number; };
  RubriekenVerlaagd: { [rubriek: number]: number; };
  ErrorCode: number;
  OptimalisatieInfo: OptimalisatieInfo;
}

export const emptyCalcResult: CalcJsonResult = {
  Versie: "unknown",
  DetailBerekeningHtml: "",
  TeBetalenBelastingHtml: "",
  BesparingLangeTermijn: 0,
  BesparingPensioensparen: 0,
  ValidatieFouten: [],
  AangifteInfo: [],
  Opmerkingen: [],
  VoorafBetalingen: [],
  Rubrieken: [],
  RubriekenVrijgesteld: [],
  RubriekenVerlaagd: [],
  ErrorCode: 0,
  OptimalisatieInfo: {
    OptimalisatieUitgevoerd: false,
    OptimalisatieBsLtsKapMogelijk: false,
    OptimalisatieWoonBonusMogelijk: false,
    RubriekOptimalisaties: [],
  },
}

const hasValue = (value: unknown) => value !== undefined && value !== null && value !== "";

const rubriekValue = (rubriek: Rubriek) => {
  if (!hasValue(rubriek.value)) return option.none;
  return pipe(
    rubriek.value,
    (value) => {
      switch (rubriek.type) {
        case "":
        case "pct":
          return (value as number).toFixed(2);
        case "num":
          return (value as number).toFixed(0);
        default:
          return value;
      }
    },
    option.some
  );
};

const isRubriekNumber = (key: string) => /[0-9]{4}/.test(key);

const mapRubriekKey = (key: string) => {
  if (isRubriekNumber(key)) return `c${key}`;

  return key;
};

const sanitizeValues = (rubrieken: RubriekReducer) => {
  return pipe(
    rubrieken,
    rubriekenFp.map(rubriekValue),
    rubriekenFp.filter(option.isSome),
    rubriekenFp.map((o) => o.value),
    rubriekenFp.toArray,
    array.map(([key, value]) => [mapRubriekKey(key), value] as [string, Value]),
    record.fromFoldable(last<Value>(), array.Foldable)
  );
};

export const fetchResult = async ({ company, locale, aangifte }: Props) => {
  let url = `${process.env.REACT_APP_API_URL}/getresult?client=${company}&lang=${locale}`;
  const body = JSON.stringify({
          ...sanitizeValues(aangifte.rubrieken),
          postalcode: aangifte.city?.postalcode,
          trate: aangifte.city?.taxRate,
          gewest: aangifte.city?.gewest,
          town: aangifte.city?.name[locale],
  });

  const response = await fetch(url, {
    headers: { "Content-Type": "application/json" },
    method: "POST",
    body: body,
  });

  return await response.text();
};

